import React, { useEffect, useRef, useState } from "react";
import { Typography, CircularProgress, Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as api from "../services/api";
import { config } from "../config/config";
import { logout, tokenPayload } from "../auth";
import { useCurrentLocation } from "../components/LocationSelector";
import { status } from "shared/src/appointment.mjs";
import { useSnackbarMessage } from "../components/Snackbar.jsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons/faTriangleExclamation";
import { useNavigate } from "../url.js";

const AUTH_TOKEN_KEY = "labs1503.auth.auth_token";
const LAST_APPOINTMENT_ID = "lastAppointmentId";

export default function Cleaning() {
  const locationId = useCurrentLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const rfidInputRef = useRef(null);
  const submittingRef = useRef(false);
  const displaySnackbarMessage = useSnackbarMessage();

  const [employee, setEmployee] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [appointmentInfo, setAppointmentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [finished, setFinished] = useState(false);

  const expectedRFIDLength = 10;

  const redirectImmediately = () => {
    logout({ navigate });
  };

  const fetchEmployeeDetails = async (username) => {
    try {
      const response = await api.getEmployeeByUsername({ username });
      const employeeData = response?.data?.employeeByUsername;

      if (employeeData) {
        setEmployeeDetails({
          first_name: employeeData.first_name,
          last_name: employeeData.last_name,
          username: employeeData.username,
        });
      }
    } catch (err) {
      console.error("Failed to fetch employee details:", err);
      if (employee) {
        setEmployeeDetails({
          first_name: employee.first_name || "",
          last_name: employee.last_name || "",
          username: employee.username || "",
        });
      }
    }
  };

  const finishCleaning = async (id) => {
    if (!id) return;
    if (submittingRef.current) return;
    submittingRef.current = true;

    try {
      await api.patchAppointment({
        id,
        status_id: status.FINISHED,
        active_step: "FINISHED",
      });

      localStorage.removeItem(LAST_APPOINTMENT_ID);
      setFinished(true);
      displaySnackbarMessage({
        title: t("cleaning.success", "Úklid byl úspěšně dokončen."),
      });

      redirectImmediately();
    } catch (err) {
      console.error("Failed to mark appointment as FINISHED:", err);
      displaySnackbarMessage({
        title: t(
          "cleaning.error",
          "Nepodařilo se označit úklid jako dokončený.",
        ),
        severity: "error",
      });
    } finally {
      submittingRef.current = false;
    }
  };

  const handleRFIDInput = async (event) => {
    const input = event.target.value.trim();
    if (!input || input.length !== expectedRFIDLength) return;

    const lastAppointmentId = localStorage.getItem(LAST_APPOINTMENT_ID);
    const currentChairId = localStorage.getItem("currentChairId");

    try {
      setLoading(true);
      const response = await fetch(`${config.apiUrl}/authenticate-rfid`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rfid: input,
          lastAppointmentId,
          currentChairId,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.token) {
          localStorage.setItem(AUTH_TOKEN_KEY, data.token);
          const decoded = tokenPayload(data.token);
          setEmployee(decoded);

          if (decoded?.username) {
            await fetchEmployeeDetails(decoded.username);
          }
        }

        const idToFinish =
          appointmentInfo?.id ||
          data?.appointment?.appointment_id ||
          lastAppointmentId;

        await finishCleaning(idToFinish);
      } else {
        displaySnackbarMessage({
          title: data.error || "Chyba při ověření karty.",
          severity: "error",
        });
      }
    } catch (err) {
      console.error("RFID scan failed:", err);
      displaySnackbarMessage({
        title: "Chyba připojení.",
        severity: "error",
      });
    } finally {
      setLoading(false);
      if (event?.target) event.target.value = "";
    }
  };

  useEffect(() => {
    const fetchAppointment = async () => {
      const lastId = localStorage.getItem(LAST_APPOINTMENT_ID);
      if (!lastId) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        const response = await api.loadCleaningAppointment({
          appointmentId: lastId,
        });
        const appointment = response?.data?.appointments?.data?.[0];

        if (appointment) {
          setAppointmentInfo(appointment);
        } else {
          setNotFound(true);
        }
      } catch (err) {
        console.error("Failed to fetch appointment:", err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointment();
  }, [locationId]);

  useEffect(() => {
    const rfidInput = rfidInputRef.current;
    if (!rfidInput) return;

    const focusRFIDInput = () => {
      if (document.activeElement !== rfidInput) rfidInput.focus();
    };

    rfidInput.addEventListener("input", handleRFIDInput);
    focusRFIDInput();
    const interval = setInterval(focusRFIDInput, 500);

    return () => {
      rfidInput.removeEventListener("input", handleRFIDInput);
      clearInterval(interval);
    };
  }, [appointmentInfo]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <Typography color="error" variant="h6">
          {t("cleaning.notFound", "Žádné křeslo není připraveno k úklidu.")}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <input
        ref={rfidInputRef}
        id="rfidCleaningInput"
        type="text"
        autoComplete="off"
        style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
      />

      <Typography
        variant="h1"
        sx={{ fontWeight: "bold", mb: 4, fontSize: "3.2rem" }}
      >
        {t("cleaning.inProgressTitle", {
          seatName: localStorage.getItem("currentChairName"),
        })}
      </Typography>

      {employeeDetails && (
        <Typography>
          {t("cleaning.cleanedBy")}
          <strong>{` ${employeeDetails.first_name} ${employeeDetails.last_name}`}</strong>
        </Typography>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          border: "1px solid",
          borderColor: "warning.main",
          backgroundColor: "#FFF5E1",
          color: "warning.main",
          fontWeight: "600",
          borderRadius: 1,
          px: 2,
          py: 2,
          my: 4,
          gap: 1,
        }}
      >
        <FontAwesomeIcon size="2x" icon={faTriangleExclamation} />
        {t("cleaning.cleaningFinishedWarning")}
      </Box>

      <Box
        sx={{
          mb: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography fontWeight="600" sx={{ mb: 1 }}>
          {t("auth.cardInstruction", "Načtěte kartu")}
        </Typography>

        <Box
          component="img"
          src="/img/rfid_login.svg"
          alt="RFID login"
          sx={{
            width: 44,
            height: 39,
          }}
        />
      </Box>

      {!finished && (
        <Button
          variant="contained"
          color="primary"
          sx={{ width: "100%" }}
          onClick={() =>
            finishCleaning(
              appointmentInfo?.id || localStorage.getItem(LAST_APPOINTMENT_ID),
            )
          }
          disabled={submittingRef.current}
        >
          {t("Dokončit úklid")}
        </Button>
      )}
    </>
  );
}
